import type { ApiNode } from '@goalbuddy/client';
import { Check, GripVertical, MoreHorizontal, Plus } from 'lucide-react';
import { openMove, setDragged } from '@/lib/ui-events';
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { toast } from 'sonner';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
} from '@/components/ui';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { areaColor } from '@/lib/area';
import { useDetail, useDone, useReopen, useStructure, useUpdateNode, useUsers, userName } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { NodeBadges, TypeIcon } from './node-meta';

const CHECKABLE = new Set(['task', 'result', 'goal', 'project', 'idea']);

/** Row-level keyboard navigation: j/k or arrows move, x completes, e renames, Enter opens. */
function moveFocus(row: HTMLElement, dir: 1 | -1) {
  const rows = Array.from(document.querySelectorAll<HTMLElement>('[data-node-row]'));
  rows[rows.indexOf(row) + dir]?.focus();
}

export function NodeRow({
  node,
  dragHandle,
  extra,
  showParent,
  parentTitle,
  inlineEdit = true,
  size = 'md',
}: {
  node: ApiNode;
  dragHandle?: ReactNode;
  extra?: ReactNode;
  showParent?: boolean;
  parentTitle?: string | null;
  inlineEdit?: boolean;
  size?: 'md' | 'lg';
}) {
  const { open, openId } = useDetail();
  const { data: users } = useUsers();
  const { data: structure } = useStructure();
  const done = useDone();
  const reopen = useReopen();
  const update = useUpdateNode();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(node.title);
  const [popping, setPopping] = useState(false);
  const isDone = node.status === 'done';
  const convert = useMutation({
    mutationFn: (type: 'task' | 'goal' | 'result') => api.convertNode(node.id, type),
  });
  const archive = useMutation({ mutationFn: () => api.archiveNode(node.id) });
  const drop = useMutation({ mutationFn: () => api.dropNode(node.id) });
  const color = showParent ? areaColor(structure, node.parent_id) : null;

  const save = () => {
    setEditing(false);
    if (title.trim() && title.trim() !== node.title) update.mutate({ id: node.id, title: title.trim() });
    else setTitle(node.title);
  };

  const toggle = () => {
    if (isDone) return reopen.mutate(node.id);
    setPopping(true);
    setTimeout(() => setPopping(false), 320);
    done.mutate(node.id, {
      onSuccess: () =>
        toast(`Done: ${node.title}`, {
          icon: '🎉',
          action: { label: 'Undo', onClick: () => reopen.mutate(node.id) },
          duration: 5000,
        }),
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget || e.metaKey || e.ctrlKey || e.altKey) return;
    const row = e.currentTarget;
    if (e.key === 'ArrowDown' || e.key === 'j') {
      e.preventDefault();
      moveFocus(row, 1);
    } else if (e.key === 'ArrowUp' || e.key === 'k') {
      e.preventDefault();
      moveFocus(row, -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      open(node.id);
    } else if (e.key === 'x' && CHECKABLE.has(node.type)) {
      e.preventDefault();
      toggle();
    } else if (e.key === 'm' && inlineEdit) {
      e.preventDefault();
      openMove(node.id);
    } else if (e.key === 'e' && inlineEdit) {
      e.preventDefault();
      setEditing(true);
    }
  };

  return (
    <div
      data-node-row
      tabIndex={0}
      onKeyDown={onKeyDown}
      className={cn(
        'group relative flex items-start gap-2.5 rounded-xl px-2.5 outline-none transition-colors hover:bg-muted/70 focus-visible:bg-muted/70 focus-visible:ring-2 focus-visible:ring-primary/40',
        size === 'lg' ? 'py-3' : 'py-2',
        openId === node.id && 'bg-accent hover:bg-accent',
        (isDone || node.status === 'dropped') && 'opacity-60',
      )}
    >
      {dragHandle ?? <span className="w-0" />}
      {CHECKABLE.has(node.type) ? (
        <button
          className={cn(
            'mt-px flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2 transition-colors',
            isDone
              ? 'border-ok bg-ok text-white'
              : 'border-muted-fg/40 text-transparent hover:border-ok hover:text-ok/60',
            popping && 'check-pop',
          )}
          aria-label={isDone ? 'Mark as not done' : 'Mark as done'}
          onClick={toggle}
        >
          <Check size={13} strokeWidth={3} />
        </button>
      ) : (
        <span className="mt-0.5 flex h-[22px] w-[22px] items-center justify-center">
          <TypeIcon type={node.type} size={16} />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {CHECKABLE.has(node.type) && node.type !== 'task' && <TypeIcon type={node.type} />}
          {editing ? (
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={save}
              onKeyDown={(e) => {
                if (e.key === 'Enter') save();
                if (e.key === 'Escape') {
                  setTitle(node.title);
                  setEditing(false);
                }
              }}
              className="h-8"
            />
          ) : (
            <button
              className={cn(
                'min-w-0 text-left font-medium leading-snug',
                size === 'lg' ? 'text-base' : 'text-[15px]',
                isDone ? 'text-muted-fg line-through' : 'text-fg',
              )}
              draggable={inlineEdit}
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', node.title);
                setDragged({ id: node.id, type: node.type });
              }}
              onDragEnd={() => setDragged(null)}
              onClick={() => open(node.id)}
              onDoubleClick={() => inlineEdit && setEditing(true)}
              title="Click to open · double-click or E to rename · drag onto the sidebar to move"
            >
              <span className="line-clamp-2">{node.title}</span>
            </button>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          {showParent && parentTitle && (
            <span className="flex max-w-[16rem] items-center gap-1 truncate text-xs text-muted-fg">
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ background: color ?? 'var(--color-muted-fg)' }}
              />
              <span className="truncate">{parentTitle}</span>
            </span>
          )}
          <NodeBadges node={node} assignee={userName(users, node.assignee_id)} />
          {inlineEdit && !node.due_date && node.type !== 'area' && node.type !== 'section' && !isDone && (
            <label className="relative hidden items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium leading-none text-muted-fg/70 hover:bg-muted hover:text-fg group-hover:flex group-focus-within:flex">
              <Plus size={11} /> date
              <input
                type="date"
                aria-label="Set due date"
                className="absolute inset-0 w-full cursor-pointer opacity-0"
                onClick={(e) => e.currentTarget.showPicker?.()}
                onChange={(e) => update.mutate({ id: node.id, due_date: e.target.value || null })}
              />
            </label>
          )}
        </div>
      </div>
      {extra}
      {inlineEdit && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 text-muted-fg/70 hover:text-fg data-[state=open]:bg-muted"
              aria-label="Actions"
            >
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setEditing(true)}>Rename</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => openMove(node.id)}>Move to…</DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => update.mutate({ id: node.id, is_next_action: !node.is_next_action })}
            >
              {node.is_next_action ? 'Unmark next action' : 'Mark as next action'}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() =>
                update.mutate({
                  id: node.id,
                  due_date: node.due_date
                    ? null
                    : new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(new Date()),
                })
              }
            >
              {node.due_date ? 'Clear due date' : 'Due today'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Status</DropdownMenuLabel>
            {(['active', 'waiting', 'done', 'dropped'] as const).map((s) => (
              <DropdownMenuItem
                key={s}
                disabled={node.status === s}
                onSelect={() => (s === 'dropped' ? drop.mutate() : update.mutate({ id: node.id, status: s }))}
              >
                {s}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Assign</DropdownMenuLabel>
            <DropdownMenuItem onSelect={() => update.mutate({ id: node.id, assignee_id: null })}>
              Unassigned
            </DropdownMenuItem>
            {users
              ?.filter((u) => u.role !== 'system')
              .map((u) => (
                <DropdownMenuItem
                  key={u.id}
                  onSelect={() => update.mutate({ id: node.id, assignee_id: u.id })}
                >
                  {u.name}
                </DropdownMenuItem>
              ))}
            {node.type === 'idea' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Convert idea</DropdownMenuLabel>
                {(['task', 'goal', 'result'] as const).map((t) => (
                  <DropdownMenuItem key={t} onSelect={() => convert.mutate(t)}>
                    to {t}
                  </DropdownMenuItem>
                ))}
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => archive.mutate()}>Archive (with children)</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

export function DragHandle(props: React.HTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className="mt-0.5 cursor-grab touch-none text-muted-fg/50 hover:text-fg"
      aria-label="Drag"
      {...props}
    >
      <GripVertical size={16} />
    </button>
  );
}
